/**
 * Cognito group names (the `cognito:groups` claim values) and the app sections
 * each group is allowed to access. Single source of truth for route-level
 * authorization — used by both the route guards and the navigation menu.
 */

export enum UserGroup {
  SCOUT = 'Scout',
  TRAINER = 'Trainer',
  ANALYST = 'Analyst',
  ADMIN = 'Admin',
}

/** Gated route paths (must match the `path` values in app.routes.ts). */
export enum AppRoute {
  PRE_GAME = 'pre-game',
  PLAYGROUND = 'playground',
  SCOUTING = 'scouting',
}

/** Human-readable section names, used in the access-denied screen / menu. */
export const ROUTE_LABELS: Record<AppRoute, string> = {
  [AppRoute.PRE_GAME]: 'My Team',
  [AppRoute.PLAYGROUND]: 'Playground',
  [AppRoute.SCOUTING]: 'Scouting',
};

/** Routes that a fully-privileged group (Admin) can access. */
const ALL_ROUTES: AppRoute[] = Object.values(AppRoute);

/**
 * Default sections granted to a user whose groups match none of the known
 * groups below (including a user with no groups at all). Equivalent to the
 * Trainer/Analyst level — they see My Team and Playground, but not Scouting.
 */
const DEFAULT_ROUTES: AppRoute[] = [AppRoute.PRE_GAME, AppRoute.PLAYGROUND];

/** Maps each group to the sections it may access. */
export const GROUP_ROUTE_ACCESS: Record<UserGroup, AppRoute[]> = {
  [UserGroup.SCOUT]: [AppRoute.SCOUTING],
  [UserGroup.TRAINER]: [AppRoute.PRE_GAME, AppRoute.PLAYGROUND],
  [UserGroup.ANALYST]: [AppRoute.PRE_GAME, AppRoute.PLAYGROUND],
  [UserGroup.ADMIN]: ALL_ROUTES,
};

/**
 * Resolve every section a user may access, as the union of all their known
 * groups. Falls back to DEFAULT_ROUTES when none of their groups are
 * recognized, so unmapped/group-less users still get the baseline sections.
 */
export function getAccessibleRoutes(groups: string[]): AppRoute[] {
  const routes: AppRoute[] = [];
  for (const group of groups) {
    const granted = GROUP_ROUTE_ACCESS[group as UserGroup];
    if (!granted) continue;
    for (const route of granted) {
      if (!routes.includes(route)) routes.push(route);
    }
  }
  return routes.length ? routes : DEFAULT_ROUTES;
}

/** True when the user's groups (or the default fallback) grant the given route. */
export function canAccessRoute(groups: string[], route: AppRoute): boolean {
  return getAccessibleRoutes(groups).includes(route);
}

/** First route the user can access (used as a safe landing/redirect target). */
export function getFirstAllowedRoute(groups: string[]): AppRoute | null {
  return getAccessibleRoutes(groups)[0] ?? null;
}
